import { api } from '@/helpers/apiClient'
import { logRequestFailure } from '@/helpers/request-outcome'
import {
  ghlAvailabilityDtoSchema,
  ghlCoupleListDtoSchema,
  ghlNameFormatDtoSchema,
  ghlPrefillDtoSchema,
  type GhlAvailabilityDto,
  type GhlCoupleListDto,
  type GhlNameFormatDto,
  type GhlPrefillDto,
} from '@lib/dto/gohighlevel.dto'
import type { GhlPrefillQuery } from '@lib/input/gohighlevel.input'
import type { IGhlLeadsService } from '@/types/GhlLeadsService.types'

const base = (venueId: string): string => `/venues/${encodeURIComponent(venueId)}/gohighlevel`

/** A venue's GoHighLevel leads, for the lead picker on the new-event form (docs/gohighlevel.spec.md). */
class GhlLeadsServiceImpl implements IGhlLeadsService {
  async availability(venueId: string): Promise<GhlAvailabilityDto> {
    try {
      return await api.get(`${base(venueId)}/availability`, ghlAvailabilityDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to check GoHighLevel for a venue', { venueId }, error)
      throw error
    }
  }

  async couples(venueId: string): Promise<GhlCoupleListDto> {
    try {
      return await api.get(`${base(venueId)}/couples`, ghlCoupleListDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to load GoHighLevel leads', { venueId }, error)
      throw error
    }
  }

  async prefill(venueId: string, query: GhlPrefillQuery): Promise<GhlPrefillDto> {
    const params = new URLSearchParams({ connectionId: query.connectionId, opportunityId: query.opportunityId })
    try {
      return await api.get(`${base(venueId)}/prefill?${params.toString()}`, ghlPrefillDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to read a GoHighLevel lead', { venueId, connectionId: query.connectionId }, error)
      throw error
    }
  }

  async nameFormat(venueId: string): Promise<GhlNameFormatDto> {
    try {
      return await api.get(`${base(venueId)}/name-format`, ghlNameFormatDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to load the event name format', { venueId }, error)
      throw error
    }
  }
}

export const ghlLeadsService: IGhlLeadsService = new GhlLeadsServiceImpl()
